import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelText as _vModelText, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "form-group"
}
const _hoisted_2 = ["value"]
const _hoisted_3 = {
  key: 1,
  class: "form-group"
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = ["value"]
const _hoisted_6 = {
  key: 2,
  class: "form-group"
}
const _hoisted_7 = ["disabled"]
const _hoisted_8 = {
  key: 3,
  class: "form-group"
}
const _hoisted_9 = {
  key: 4,
  class: "form-group"
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["value"]
const _hoisted_14 = {
  key: 5,
  class: "form-group"
}
const _hoisted_15 = {
  key: 6,
  class: "form-group"
}
const _hoisted_16 = { class: "form-group errors" }
const _hoisted_17 = {
  key: 0,
  class: "error"
}

import { computed, reactive, watch } from 'vue';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { Lov } from 'src/models/master';
import type { AdminUser } from 'src/models/user';
import type {
  AdminUserCreateParams,
  AdminUserPasswordUpdateParams,
  AdminUserUpdateParams,
} from 'src/models/apis/user/adminUserRequest';
import { useAuth } from 'src/composables/useAuth';

const PASSWORD_MIN_LENGTH = 8;

interface UserEditForm {
  id: number;
  group2Id: number | null;
  groupId: number | null;
  username: string;
  displayName: string;
  role: string | null;
  password: string;
  passwordConfirmation: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UserEditModal',
  props: {
    editMode: {},
    editingUser: {},
    groups2: {},
    groups3: {},
    selectableRoles: {},
    httpErrorMsgs: {}
  },
  emits: ["create", "updateProfile", "updatePassword", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const initEditingState = (): UserEditForm => {
  return {
    id: 0,
    group2Id: null,
    groupId: null,
    username: '',
    displayName: '',
    role: null,
    password: '',
    passwordConfirmation: '',
  };
};

// パスワード変更では groupId は user.groupId、編集では groupInfo.g3id を使う（既存挙動を踏襲）
const buildEditForm = (user: AdminUser, groupId: number | null): UserEditForm => {
  return {
    id: user.id,
    group2Id: user.groupInfo.g2id,
    groupId,
    username: user.username,
    displayName: user.displayName,
    role: user.role.toString(),
    password: '',
    passwordConfirmation: '',
  };
};

const state = reactive<{ editForm: UserEditForm; createOrUpdatePasswordOkPushed: boolean }>({
  editForm: initEditingState(),
  createOrUpdatePasswordOkPushed: false,
});

const auth = useAuth();

const isValidPasswordString = (password: string): boolean => {
  const regAlphaLower = /[a-z]/;
  const regAlphaUpper = /[A-Z]/;
  const regNumber = /[0-9]/;
  const regSymbol = /[!@#$%^&*(){}[\]:;"'<>,.?/~`_+\-=|\\ ]/;
  // 3種類以上の文字種を含めることを強制する.
  let numStrKinds = 0;
  if (password.match(regAlphaLower)) { numStrKinds++; }
  if (password.match(regAlphaUpper)) { numStrKinds++; }
  if (password.match(regNumber)) { numStrKinds++; }
  if (password.match(regSymbol)) { numStrKinds++; }
  return numStrKinds >= 3;
};

const requiresProfileFields = computed(() => {
  return props.editMode === 'create' || props.editMode === 'edit';
});
const requiresPasswordFields = computed(() => {
  return props.editMode === 'create' || props.editMode === 'editPassword';
});
const filteredGroups3 = computed<AdminGroup3[]>(() => {
  return props.groups3.filter(group3 => group3.parentId === state.editForm.group2Id);
});
const validateEditing = computed(() => {
  const errorMsgs: string[] = [];
  if (requiresProfileFields.value &&
    (!state.editForm.group2Id || !Number.isInteger(state.editForm.group2Id))
  ) {
    errorMsgs.push('事業所は選択必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.groupId || !Number.isInteger(state.editForm.groupId))
  ) {
    errorMsgs.push('部署は選択必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.username || state.editForm.username.length <= 0)
  ) {
    errorMsgs.push('ユーザーIDは必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.displayName || state.editForm.displayName.length <= 0)
  ) {
    errorMsgs.push('ユーザー名は必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.role || !Number.isInteger(parseInt(state.editForm.role)))
  ) {
    errorMsgs.push('権限は必須です');
  }
  if (requiresPasswordFields.value) {
    if (!state.editForm.password || state.editForm.password.length < PASSWORD_MIN_LENGTH) {
      errorMsgs.push(`パスワードは${PASSWORD_MIN_LENGTH}文字以上にしてください`);
    }
    if (state.editForm.password && !isValidPasswordString(state.editForm.password)) {
      errorMsgs.push('パスワードには英小文字、英大文字、数字、記号から3種類以上の文字種を含めてください。');
    }
  }
  return errorMsgs;
});
const passwordErrorMsg = computed(() => {
  if (requiresPasswordFields.value &&
    state.editForm.password !== state.editForm.passwordConfirmation) {
    return 'パスワードが一致しません。';
  }
  return null;
});
const selectedGroup2 = computed(() => {
  return state.editForm.group2Id != null;
});
const editModalTitle = computed(() => {
  if (props.editMode === 'create') {
    return '新規作成';
  } else if (props.editMode === 'editPassword') {
    return 'パスワード変更';
  } else {
    return '編集';
  }
});
const isStrongerRoleThanMe = computed(() => {
  if (!auth.user) return false;
  if (!state.editForm.id || !state.editForm.role) return false;

  const otherUserRole = parseInt(state.editForm.role);
  return otherUserRole < auth.user.role;
});
const isLoginUser = computed(() => {
  return !!auth.user && auth.user.id === state.editForm.id;
});

const save = (): void => {
  if (requiresPasswordFields.value) {
    state.createOrUpdatePasswordOkPushed = true;
    if (passwordErrorMsg.value) return;
  }
  if (props.editMode === 'edit') {
    if (!state.editForm.id ||
      !state.editForm.username ||
      !state.editForm.displayName ||
      !state.editForm.role ||
      !state.editForm.groupId
    ) {
      return;
    }
    emit('updateProfile', state.editForm.id, {
      username: state.editForm.username,
      display_name: state.editForm.displayName || '',
      role: parseInt(state.editForm.role),
      group_id: state.editForm.groupId,
    });
  } else if (props.editMode === 'editPassword') {
    if (!state.editForm.id) return;

    emit('updatePassword', state.editForm.id, {
      password: state.editForm.password || '',
      password_confirmation: state.editForm.passwordConfirmation || '',
    });
  } else {
    if (!state.editForm.role ||
      !state.editForm.groupId ||
      !state.editForm.password ||
      !state.editForm.passwordConfirmation
    ) {
      return;
    }
    emit('create', {
      id: state.editForm.id,
      username: state.editForm.username,
      display_name: state.editForm.displayName,
      role: parseInt(state.editForm.role),
      group_id: state.editForm.groupId,
      password: state.editForm.password,
      password_confirmation: state.editForm.passwordConfirmation,
    });
  }
};

watch(() => [props.editMode, props.editingUser] as const, ([editMode, editingUser]) => {
  state.createOrUpdatePasswordOkPushed = false;
  if (editMode === 'create' || !editingUser) {
    state.editForm = initEditingState();
    return;
  }
  const groupId = editMode === 'editPassword' ? editingUser.groupId : editingUser.groupInfo.g3id;
  state.editForm = buildEditForm(editingUser, groupId);
}, { immediate: true });

return (_ctx: any,_cache: any) => {
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createBlock(_component_Modal, {
    title: editModalTitle.value,
    "bottom-close-button-label": "保存",
    isCloseButtonDisabled: validateEditing.value.length > 0,
    onClose: save,
    onDismiss: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('close')))
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", null, [
        (__props.editMode === 'create' || __props.editMode === 'edit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _cache[9] || (_cache[9] = _createElementVNode("label", { class: "form-control-label" }, "事業所", -1)),
              _withDirectives(_createElementVNode("select", {
                class: "form-control",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((state.editForm.group2Id) = $event)),
                onChange: _cache[1] || (_cache[1] = ($event: any) => (state.editForm.groupId = null))
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.groups2, (e) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: e.id,
                    value: e.id
                  }, _toDisplayString(e.name), 9, _hoisted_2))
                }), 128))
              ], 544), [
                [_vModelSelect, state.editForm.group2Id]
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'edit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _cache[10] || (_cache[10] = _createElementVNode("label", { class: "form-control-label" }, "部署", -1)),
              _withDirectives(_createElementVNode("select", {
                class: "form-control",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((state.editForm.groupId) = $event)),
                disabled: !selectedGroup2.value
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGroups3.value, (e) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: e.id,
                    value: e.id
                  }, _toDisplayString(e.name), 9, _hoisted_5))
                }), 128))
              ], 8, _hoisted_4), [
                [_vModelSelect, state.editForm.groupId]
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'edit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _cache[11] || (_cache[11] = _createElementVNode("label", { class: "form-control-label" }, " ユーザーID ", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                class: "form-control",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((state.editForm.username) = $event)),
                disabled: __props.editMode === 'edit'
              }, null, 8, _hoisted_7), [
                [_vModelText, state.editForm.username]
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'edit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _cache[12] || (_cache[12] = _createElementVNode("label", { class: "form-control-label" }, " ユーザー名 ", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                class: "form-control",
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((state.editForm.displayName) = $event))
              }, null, 512), [
                [_vModelText, state.editForm.displayName]
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'edit')
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _cache[15] || (_cache[15] = _createElementVNode("label", { class: "form-control-label" }, " 権限 ", -1)),
              (isLoginUser.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [...(_cache[13] || (_cache[13] = [
                    _createElementVNode("span", { class: "text-danger" }, " ※自分の権限を変更することはできません ", -1)
                  ]))]))
                : _createCommentVNode("", true),
              (isStrongerRoleThanMe.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [...(_cache[14] || (_cache[14] = [
                    _createElementVNode("span", { class: "text-danger" }, " ※このユーザーの権限を変更することはできません ", -1)
                  ]))]))
                : _createCommentVNode("", true),
              _withDirectives(_createElementVNode("select", {
                class: "form-control",
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((state.editForm.role) = $event)),
                disabled: isStrongerRoleThanMe.value || isLoginUser.value
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.selectableRoles, (e) => {
                  return (_openBlock(), _createElementBlock("option", {
                    key: e.key,
                    value: e.key
                  }, _toDisplayString(e.val), 9, _hoisted_13))
                }), 128))
              ], 8, _hoisted_12), [
                [_vModelSelect, state.editForm.role]
              ])
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'editPassword')
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _cache[16] || (_cache[16] = _createElementVNode("label", { class: "form-control-label" }, " パスワード ", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "password",
                class: "form-control",
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((state.editForm.password) = $event)),
                autocomplete: "new-password"
              }, null, 512), [
                [_vModelText, state.editForm.password]
              ]),
              _cache[17] || (_cache[17] = _createElementVNode("span", { class: "ib ml8 text-warning password-condition-warning" }, [
                _createElementVNode("div", null, "※パスワードは8文字以上で入力してください"),
                _createElementVNode("div", null, "※パスワードには英小文字、英大文字、数字、記号から3種類以上の文字種を含めてください。")
              ], -1))
            ]))
          : _createCommentVNode("", true),
        (__props.editMode === 'create' || __props.editMode === 'editPassword')
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
              _cache[18] || (_cache[18] = _createElementVNode("label", { class: "form-control-label" }, " パスワード確認 ", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "password",
                class: "form-control",
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((state.editForm.passwordConfirmation) = $event)),
                autocomplete: "new-password"
              }, null, 512), [
                [_vModelText, state.editForm.passwordConfirmation]
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_16, [
          (passwordErrorMsg.value != null && state.createOrUpdatePasswordOkPushed)
            ? (_openBlock(), _createElementBlock("div", _hoisted_17, _toDisplayString(passwordErrorMsg.value), 1))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.httpErrorMsgs, (e, i) => {
            return (_openBlock(), _createElementBlock("div", {
              class: "error",
              key: i
            }, _toDisplayString(e), 1))
          }), 128))
        ])
      ])
    ]),
    _: 1
  }, 8, ["title", "isCloseButtonDisabled"]))
}
}

})